import type { EmployerHistoryRow, WorkNumberEligibility, WorkNumberHistory } from "./worknumber.types";

// ---------------------------------------------------------------------------
// worknumber.mapping
//
// Pure mappers: the generated client returns `object` for every Work Number
// endpoint, so these translate the raw payloads into the app's view models. No
// React. Unit-testable and side-effect-free. Every field is read defensively —
// unknown/missing keys degrade to `false` (eligibility) or an em-dash cell
// (history) rather than throwing, so BFF field-name drift never crashes the UI.
// ---------------------------------------------------------------------------

const EMPTY_CELL = "—";

/**
 * Narrows an unknown payload to a string-keyed record, or `{}` when it is not a
 * plain object (null, array, primitive).
 * @param {unknown} value - The raw payload.
 * @returns {Record<string, unknown>} The record, or an empty object.
 */
function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

/**
 * Reads the first present key from a record and renders it as a display string.
 * Numbers are stringified; null/undefined/empty become the em-dash placeholder.
 * @param {Record<string, unknown>} source - The row payload.
 * @param {...string} keys - Candidate field names in priority order.
 * @returns {string} The display value, or "—".
 */
function readCell(source: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== "") {
      return String(value);
    }
  }
  return EMPTY_CELL;
}

// A manual-override path (OR-23450): even when the standard eligible flag is
// false, the applicant still qualifies to pull if every one of these BFF-supplied
// checks passes.
const MANUAL_OVERRIDE_FLAGS = [
  "rolePass",
  "statusIsPd",
  "noPriorVerification",
  "notPlatinum",
  "notGoldHighScore",
] as const;

/**
 * Maps the eligibility payload down to the single decision the UI needs. Treats
 * the applicant as eligible when any recognised "eligible" flag is truthy, OR
 * every manual-override flag (MANUAL_OVERRIDE_FLAGS) is truthy; the untouched
 * payload is preserved on `raw` for later flag surfacing. Also surfaces the
 * business-level `lastPullStatus` (e.g. `"NOTFOUND"`), when present, so callers
 * can warn the analyst that no purchased employer data has ever been found for
 * this applicant.
 * @param {unknown} payload - The raw `/eligibility` response.
 * @returns {WorkNumberEligibility} The reduced eligibility view model.
 */
export function mapEligibility(payload: unknown): WorkNumberEligibility {
  const raw = asRecord(payload);
  const eligible = Boolean(raw.canPull ?? raw.eligible ?? raw.isEligible);
  const manualOverride = MANUAL_OVERRIDE_FLAGS.every((flag) => Boolean(raw[flag]));
  const lastPullStatus = typeof raw.lastPullStatus === "string" ? raw.lastPullStatus : undefined;
  return { canPull: eligible || manualOverride, lastPullStatus, raw };
}

/**
 * Maps a single raw history entry to a fully-populated table row, filling any
 * missing column with an em-dash.
 * @param {unknown} entry - One raw history record.
 * @returns {EmployerHistoryRow} The table row.
 */
function mapRow(entry: unknown): EmployerHistoryRow {
  const row = asRecord(entry);
  return {
    employerName: readCell(row, "employerName"),
    employeeStatus: readCell(row, "employeeStatus", "employmentStatus"),
    occupation: readCell(row, "occupation"),
    hireDate: readCell(row, "hireDate"),
    // recap-service's real /history payload sends the pay-frequency and
    // pay-rate-basis descriptors as `payFrequencyDesc` / `payRateDesc` — the
    // bare `payFrequency` / `payPeriodFrequency` keys are only ever seen in
    // fixtures, never from the live BFF.
    payFrequency: readCell(row, "payFrequency", "payFrequencyDesc"),
    payRate: readCell(row, "payRate"),
    payPeriodFrequency: readCell(row, "payPeriodFrequency", "payRateDesc"),
  };
}

/**
 * Maps a work-number history/pull payload into table rows. Accepts either a bare
 * array of entries or an object wrapping the entries under a common key
 * (`rows` / `history` / `employments` / `employers`); anything else yields no rows.
 *
 * Both `WorkbenchWorknumberApi.getHistory` and `.pullWorkNumber` wrap the actual
 * recap-service body one level deeper, under a top-level `data` key (per the
 * BFF contract notes: "recap's body is returned under 'data', not at the top
 * level") — so `payload.data` is unwrapped first, before the rows/history/
 * employments/employers candidates are checked, else a well-formed response
 * with real rows maps to an empty table.
 * @param {unknown} payload - The raw `/history` or POST `/worknumber` response.
 * @returns {WorkNumberHistory} The history view model.
 */
export function mapHistory(payload: unknown): WorkNumberHistory {
  const entries = Array.isArray(payload)
    ? payload
    : (() => {
        const record = asRecord(payload);
        // Unwrap the BFF's `data` envelope first (if present) so the rows/
        // history/employments/employers lookup below runs against the actual
        // recap body rather than the outer `{ data, stipulationPanel }` shell.
        const unwrapped = "data" in record ? record.data : record;
        const source = Array.isArray(unwrapped) ? { rows: unwrapped } : asRecord(unwrapped);
        const candidate = source.rows ?? source.history ?? source.employments ?? source.employers;
        return Array.isArray(candidate) ? candidate : [];
      })();
  return { rows: entries.map((entry) => mapRow(entry)) };
}

/**
 * Reads recap-service's business-level `data.status` field from a pull/history
 * payload (e.g. `"NOTFOUND"` when the recap lookup found no match), as distinct
 * from the HTTP status of the response itself. recap can return HTTP 202 with
 * an empty `employers` array and this status field explaining why — callers use
 * this to surface a warning toast instead of silently rendering nothing.
 * @param {unknown} payload - The raw `/history` or POST `/worknumber` response body.
 * @returns {string | undefined} The business status, if present and a string.
 */
export function extractDataStatus(payload: unknown): string | undefined {
  const data = asRecord(payload).data;
  const status = asRecord(data).status;
  return typeof status === "string" ? status : undefined;
}
